/**
 * The version guard: one hex number that changes whenever this build would fly
 * a recorded stick differently.
 *
 * ## Why it is measured rather than declared
 *
 * The obvious guard is a hand-maintained integer — `SNAPSHOT_VERSION`, bumped
 * by whoever changes the model. That works for *serialization* (a field either
 * exists in the file or it does not) and it is exactly wrong for *behaviour*,
 * because the changes that break a tape are the ones nobody thinks of as
 * breaking changes: a drag coefficient nudged 2 %, a tunable recalibrated
 * against DOSBox, a new manoeuvre in the AI's table, a wind term added to the
 * force balance. None of those touch the snapshot layout, all of them make
 * every existing tape replay into a different flight, and none of them will
 * reliably be accompanied by someone remembering to bump a constant.
 *
 * So the fingerprint is taken the only way that cannot be forgotten: this build
 * is made to **fly a short fixed flight and a short fixed fight**, and the state
 * hashes it lands on are the fingerprint. Any change to the integrator, the
 * aerofoil tables, the tunables, the RNG stream set, the encounter pass order,
 * the AI, the guns or the flak moves the number without anyone deciding that it
 * should.
 *
 * ## What it does not claim
 *
 * It is not a checksum of the source. Two builds that differ only in a comment
 * fingerprint identically, which is correct — such a build replays a tape
 * faithfully. And per AD-007a it is not a cross-platform identity: floating
 * point is pinned to a hardware/browser family, not to the world, so a tape
 * carried to a different machine may refuse honestly rather than diverge
 * quietly. Refusing is the desired failure.
 *
 * The probes are deliberately small — a few hundred ticks — because this runs
 * when a tape is saved and again when one is loaded. See `replay.md` for the
 * measured cost.
 */

import { encounterHash } from "../combat/encounter-hash.ts";
import { createEncounter, stepEncounter, type EncounterSetup } from "../combat/encounter.ts";
import { contentHash } from "../flight/hash.ts";
import { makeInputs, type FlightInputs } from "../flight/inputs.ts";
import { RNG_STREAM_NAMES } from "../flight/rng.ts";
import { simStateHash, SNAPSHOT_VERSION } from "../flight/snapshot.ts";
import { createSim, type SimState } from "../flight/state.ts";
import { ALL_REALISM_ON } from "../flight/tiers.ts";
import { step } from "../flight/step.ts";

/** Ticks of solo flight in the airframe probe. Three seconds is past the first transient and well inside a stall. */
const AIRFRAME_PROBE_TICKS = 180;

/** Ticks of dogfight in the encounter probe — long enough for the AI to commit to a manoeuvre and the guns to fire. */
const ENCOUNTER_PROBE_TICKS = 240;

/**
 * A stick that moves every axis and works every optional subsystem, so a change
 * to any of them shows up. Pure in the tick number: the probe must not depend
 * on the state it is probing, or a model change could cancel itself out.
 */
function probeInputs(tick: number): FlightInputs {
  return makeInputs({
    pitch: Math.sin(tick / 31) * 0.6,
    roll: Math.cos(tick / 17) * 0.5,
    yaw: Math.sin(tick / 53) * 0.3,
    throttle: 0.6 + 0.4 * Math.sin(tick / 71),
    firing: tick % 23 < 9,
    clearJam: tick % 61 === 0,
  });
}

/**
 * One aeroplane, every realism switch on, flown hard for three seconds. Covers
 * the integrator, the aerodynamics, the engine, the guns, the physiology, the
 * damage model and the airframe's own random streams.
 */
function airframeProbe(): string {
  let sim: SimState = createSim({
    aircraft: "CAMEL",
    tier: "expert",
    realism: ALL_REALISM_ON,
    seed: 0x5eed_1917,
    altitudeM: 2400,
  });
  for (let tick = 0; tick < AIRFRAME_PROBE_TICKS; tick++) sim = step(sim, probeInputs(tick));
  return simStateHash(sim);
}

/**
 * A Camel, an Albatros astern of it, a balloon to shoot at and a battery
 * shooting back. Everything the airframe probe cannot see: the six-pass
 * encounter order, the AI, hit allocation, tracers, targets and flak.
 */
const ENCOUNTER_PROBE: EncounterSetup = {
  seed: 0x1917_5eed,
  combatLevel: "AVERAGE",
  aircraft: [
    {
      id: "probe-player",
      side: "friendly",
      pilot: { kind: "player" },
      flight: { aircraft: "CAMEL", tier: "expert", realism: ALL_REALISM_ON, altitudeM: 1200, throttle: 1, trimmed: true },
    },
    {
      id: "probe-enemy",
      side: "hostile",
      pilot: { kind: "ai", skill: "ace" },
      flight: { aircraft: "ALBA_D3", tier: "expert", realism: ALL_REALISM_ON, altitudeM: 1400, throttle: 1, trimmed: true },
      offsetM: { x: 0, y: 0, z: 400 },
    },
  ],
  targets: [
    {
      id: "probe-balloon",
      kind: "balloon",
      side: "hostile",
      position: { x: 600, y: 900, z: -1200 },
      headingRad: 0,
      label: "PROBE",
    },
  ],
  flak: [{ id: "probe-flak", side: "hostile", position: { x: 400, y: 0, z: -900 }, batteryClass: "heavy" }],
};

function encounterProbe(): string {
  let state = createEncounter(ENCOUNTER_PROBE);
  for (let tick = 0; tick < ENCOUNTER_PROBE_TICKS; tick++) {
    state = stepEncounter(state, { "probe-player": probeInputs(tick) });
  }
  return encounterHash(state);
}

let cached: string | null = null;

/**
 * This build's behavioural fingerprint. Memoized: the probes are pure, so the
 * answer cannot change within a process, and a tape list showing twenty tapes
 * must not fly the probes twenty times.
 */
export function engineFingerprint(): string {
  if (cached === null) {
    cached = contentHash(
      [SNAPSHOT_VERSION, RNG_STREAM_NAMES.join(","), airframeProbe(), encounterProbe()].join("|"),
    );
  }
  return cached;
}
